import { useLazyQuery, useQuery } from "@apollo/client";
import {
  IonCol,
  IonContent,
  IonGrid,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRow,
  IonText
} from "@ionic/react";
import equal from "deep-equal";
import { ComponentProps, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Button from "@components/buttons/Button";
import ClientAvatar from "@components/ClientAvatar";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import styles from "@components/EverpresentTasks.module.css";
import ExpertAvatar from "@components/ExpertAvatar";
import ToggleCheckbox from "@components/fields/ToggleCheckbox";
import TaskFilters from "@components/forms/TaskFilters";
import TaskForm from "@components/forms/TaskForm";
import HandleQuery from "@components/HandleQuery";
import Nbsp from "@components/Nbsp";
import TaskPopover from "@components/TaskPopover";
import Icon from "@components/text/Icon";
import StatusText from "@components/text/StatusText";
import Heading3 from "@components/typography/Heading3";
import useSubscribeToJourneyTasks from "@hooks/subscriptions/useSubscribeToJourneyTasks";
import useGetMyTimeWithTimezoneAbbreviation from "@hooks/useGetMyTimeWithTimezoneAbbreviation";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import { JourneyStatusEnum, OpenerTypeEnum } from "@typing/Enums";
import {
  GetJourneyForTasksDocument,
  GetJourneyForTasksQuery,
  GetJourneyForTasksQueryVariables,
  GetJourneyTasksDocument,
  GetJourneyTasksQuery,
  GetJourneyTasksQueryVariables,
  GoalStatusEnum,
  TaskFragment as Task,
  TaskActorKindEnum,
  TaskAssigneeEnum,
  TaskDestroyDocument,
  TaskDestroyMutation,
  TaskDestroyMutationVariables,
  TaskStatusEnum,
  TaskToggleCompletedDocument,
  TaskToggleCompletedMutation,
  TaskToggleCompletedMutationVariables
} from "@typing/Generated";
import { handleTrapFocus } from "@utils/keyboardUtils";
import {
  expertRolesWithoutBackups,
  groupByTimestampDay,
  primaryExpertRole,
  showDraft,
  sortByTimestamp,
  translationForTask
} from "@utils/modelUtils";
import { dateStringForGroup, stringForDateTime } from "@utils/timeUtils";

type Filters = ComponentProps<typeof TaskFilters>["filters"];

type Props = {
  journeyId: string;
};

const EverpresentTasks = ({ journeyId }: Props) => {
  const intl = useIntl();
  const getMyTimeWithTimezoneAbbreviation = useGetMyTimeWithTimezoneAbbreviation();
  const messagesOpener = useGlobalOpener(OpenerTypeEnum.MESSAGES);
  const tasksOpener = useGlobalOpener(OpenerTypeEnum.TASKS);

  const expertTaskOpener = useOpener();
  const deleteTaskOpener = useOpener();
  const tasksExpandOpener = useOpener();
  const messagesIsOpen = messagesOpener.isOpen;
  const tasksIsOpen = tasksOpener.isOpen;
  const queryParams = new URLSearchParams(location.search);

  const hideFab = queryParams.has("hideFab");

  const taskContentRef = useRef<HTMLIonContentElement>(null);
  const tasksRef = useRef<HTMLDivElement>(null);

  const defaultDate = new Date();

  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filters, setFilters] = useState<Filters>({
    assignee: TaskAssigneeEnum.ALL,
    clientId: null,
    status: TaskStatusEnum.INCOMPLETE
  });

  const [destroyTask] = useMutationWithValidation<TaskDestroyMutation, TaskDestroyMutationVariables>(
    TaskDestroyDocument,
    "task"
  );

  const handleTaskDestroy = useCallback(() => {
    if (editingTask) {
      destroyTask({
        id: editingTask.id
      });
    }
  }, [editingTask, destroyTask]);

  const handleTaskCreate = useCallback(() => {
    setEditingTask(null);
    expertTaskOpener.open();
  }, [expertTaskOpener]);

  const [getJourneyTasks, getJourneyTasksQuery] = useLazyQuery<GetJourneyTasksQuery, GetJourneyTasksQueryVariables>(
    GetJourneyTasksDocument,
    { variables: { journeyId } }
  );

  useSubscribeToJourneyTasks(journeyId, () => {
    getJourneyTasksQuery.refetch();
  });

  useEffect(() => {
    getJourneyTasks({
      variables: {
        assignee: filters.assignee,
        journeyId,
        status: filters.status
      }
    });
  }, [getJourneyTasks, journeyId, filters.assignee, filters.status]);

  const [toggleTaskCompleted, apolloClient] = useMutationWithValidation<
    TaskToggleCompletedMutation,
    TaskToggleCompletedMutationVariables
  >(TaskToggleCompletedDocument, "task", {
    update: () => {
      // This kicks out all of the journeyTasks queries so we will re-make them. If you're flipping between filters
      // then the queries related to them will be cached, but if completed changed then we need to redo those queries.
      apolloClient.cache.evict({ fieldName: "journeyTasks", id: "ROOT_QUERY" });
    }
  });

  const handleTaskToggleCompleted = (task: Task) => {
    if (task) {
      toggleTaskCompleted({
        completed: !task.completed,
        id: task.id
      });
    }
  };

  const tasks = useNodesFromConnection(getJourneyTasksQuery.data?.journeyTasks);

  const handleCloseTasks = useCallback(() => {
    tasksExpandOpener.close();
    expertTaskOpener.close();
    tasksOpener.close();
  }, [expertTaskOpener, tasksExpandOpener, tasksOpener]);

  const handleOpenTasks = useCallback(() => {
    tasksOpener.open();
  }, [tasksOpener]);

  const sortedTasksByDay = useMemo(
    () =>
      groupByTimestampDay<Task>(
        tasks,
        task => task.dueTime,
        intl,
        filters.status === TaskStatusEnum.COMPLETED ? false : true
      ),
    [tasks, intl, filters.status]
  );

  const handleExpandToggle = useCallback(
    (event: MouseEvent<HTMLIonRouterLinkElement>) => {
      event.stopPropagation();
      event.preventDefault();
      tasksExpandOpener.toggle();
    },
    [tasksExpandOpener]
  );

  async function searchNext(event: CustomEvent<void>) {
    await getJourneyTasksQuery.fetchMore({
      variables: { cursor: getJourneyTasksQuery.data?.journeyTasks.pageInfo?.endCursor ?? "" }
    });

    (event.target as HTMLIonInfiniteScrollElement).complete();
  }

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const getJourneyForTasksQuery = useQuery<GetJourneyForTasksQuery, GetJourneyForTasksQueryVariables>(
    GetJourneyForTasksDocument,
    {
      variables: { id: journeyId }
    }
  );

  const primaryExpert = useMemo(
    () => primaryExpertRole(getJourneyForTasksQuery.data?.journey.expertRoles)?.expert,
    [getJourneyForTasksQuery.data?.journey.expertRoles]
  );

  if (hideFab) {
    return null;
  }

  return (
    <HandleQuery hideLoader query={getJourneyForTasksQuery}>
      {journeyData => (
        <>
          <div
            className={`${styles.everpresent} ${tasksExpandOpener.isOpen ? styles.fullScreenEverpresent : ""} ${
              tasksIsOpen ? "" : "ion-hide"
            } ${messagesIsOpen ? styles.messagesOpenTaskOpen : ""}`}
            data-test="tasks"
            onKeyUp={event => {
              handleTrapFocus(event, tasksRef, handleCloseTasks);
            }}
            ref={tasksRef}
          >
            <div
              className={`${tasksIsOpen ? styles.header : styles.headerWhite} ${
                tasksExpandOpener.isOpen ? styles.fullScreenHeader : tasksIsOpen ? styles.expandedHeader : ""
              }`}
              data-test="tasks-header"
              onClick={!tasksIsOpen ? handleOpenTasks : handleCloseTasks}
              role="complementary"
            >
              <div className={styles.iconWithUnread}>
                <div>
                  <Icon color="gray" icon="clipboardListCheck" size="large" />
                </div>
                {journeyData.journey.tasksDueTodayCount !== 0 && journeyData.journey.tasksDueTodayCount !== null && (
                  <div className={styles.notification}>
                    <IonText>{journeyData.journey.tasksDueTodayCount.toString()}</IonText>
                  </div>
                )}
              </div>
              <IonText>
                <FormattedMessage id="models.tasks.taskListLabel" />
              </IonText>
              <div className={styles.icons}>
                {tasksIsOpen && (
                  <>
                    {expertRolesWithoutBackups(journeyData.journey.expertRoles)?.map(role => {
                      if (role.expert.photoUrl) {
                        return (
                          <div key={role.id}>
                            <ExpertAvatar expert={role.expert} />
                          </div>
                        );
                      }
                      return null;
                    })}
                    {journeyData.journey.clientRoles.map(role => (
                      <div key={role.id}>
                        <ClientAvatar
                          clientId={role.client.id}
                          color="primary"
                          firstName={role.client.firstName}
                          journeyId={journeyId}
                          lastName={role.client.lastName}
                          overrideIndex={sortByTimestamp(journeyData.journey.clientRoles, r => r.createdAt).findIndex(
                            r => r.client.id === role.client.id
                          )}
                        />
                      </div>
                    ))}
                    <div className={styles.toggleExpand}>
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.toggle"
                        icon={tasksExpandOpener.isOpen ? "compress" : "expand"}
                        onClick={handleExpandToggle}
                      />
                    </div>
                  </>
                )}
                <div className={styles.toggleExpand}>
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.toggleTasks"
                    color={tasksIsOpen ? undefined : "white"}
                    icon={tasksIsOpen ? "chevronDown" : "chevronUp"}
                    onClick={tasksIsOpen ? handleCloseTasks : undefined}
                  />
                </div>
              </div>
            </div>
            {expertTaskOpener.isOpen ? (
              <div className={styles.form}>
                <IonContent className={styles.scroll} ref={taskContentRef} scrollY>
                  <TaskForm
                    autofocus={expertTaskOpener.isOpen}
                    journey={journeyData.journey}
                    onCancel={expertTaskOpener.close}
                    onSuccess={expertTaskOpener.close}
                    primaryExpertId={primaryExpert?.id}
                    refetch={getJourneyTasksQuery.refetch}
                    task={editingTask}
                  />
                </IonContent>
              </div>
            ) : (
              <div
                className={
                  tasksExpandOpener.isOpen
                    ? styles.fullScreenContent
                    : tasksIsOpen
                      ? styles.expandedContent
                      : "ion-hide"
                }
              >
                <HandleQuery hideLoader query={getJourneyTasksQuery}>
                  {_data => (
                    <IonContent className={styles.scroll} ref={taskContentRef} scrollY>
                      <div className="ion-margin-start">
                        <TaskFilters filters={filters} hideClientId onChange={handleFilterChange} />
                      </div>

                      {sortedTasksByDay.map(group => (
                        <div className={styles.group} key={`${group.year}-${group.month}-${group.date}`}>
                          <div className={styles.dateGroup}>{dateStringForGroup(group.models[0].dueTime, intl)}</div>
                          {group.models.map(task => (
                            <div className={styles.task} key={task.id}>
                              <IonGrid>
                                <IonRow className="ion-align-items-center">
                                  <IonCol size="auto">
                                    <ToggleCheckbox
                                      checked={task.completed}
                                      disabled={
                                        task.clientVisible &&
                                        task.goal?.status !== GoalStatusEnum.ACTIVE &&
                                        !task.completed
                                      }
                                      hoverContent={
                                        task.clientVisible
                                          ? intl.formatMessage({ id: "pages.expert.tasks.cannotUpdateClientVisible" })
                                          : undefined
                                      }
                                      name="markComplete"
                                      onClick={() => handleTaskToggleCompleted(task)}
                                    />
                                  </IonCol>
                                  <IonCol>
                                    <IonText>
                                      {translationForTask(task, intl, getMyTimeWithTimezoneAbbreviation)}
                                      {task.actorKind === TaskActorKindEnum.AUTOMATION && (
                                        <IonText className={styles.autoAdded}>
                                          <Nbsp />
                                          <FormattedMessage id="pages.carePlan.autoAdded" />
                                        </IonText>
                                      )}
                                    </IonText>
                                  </IonCol>
                                  <IonCol size="auto">
                                    <div>
                                      <div className={styles.overdueGroup}>
                                        <Heading3 bold color="gray-500" noMargin>
                                          {stringForDateTime(task.dueTime, intl)}
                                        </Heading3>
                                        {defaultDate > task.dueTime && !task.completed && (
                                          <div className={styles.alarmIconSpacing}>
                                            <Icon color="danger" icon="alarmClock" />
                                          </div>
                                        )}
                                      </div>
                                      {showDraft(task) && (
                                        <div className={styles.draft}>
                                          <StatusText status={JourneyStatusEnum.DRAFT} />
                                        </div>
                                      )}
                                    </div>
                                  </IonCol>
                                  <IonCol size="auto">
                                    {task.clientVisible ? (
                                      <Icon
                                        color="primary-500"
                                        hoverContent={<FormattedMessage id="pages.expert.tasks.clientVisible" />}
                                        icon="eye"
                                      />
                                    ) : (
                                      <Icon
                                        color="secondary-500"
                                        hoverContent={<FormattedMessage id="pages.expert.tasks.clientInvisible" />}
                                        icon="eyeSlash"
                                      />
                                    )}
                                  </IonCol>
                                  {task.expert.photoUrl && (
                                    <IonCol size="auto">
                                      <ExpertAvatar expert={task.expert} />
                                    </IonCol>
                                  )}
                                  <IonCol size="auto">
                                    <TaskPopover
                                      handleEdit={t => {
                                        setEditingTask(t);
                                        expertTaskOpener.open();
                                      }}
                                      task={task}
                                    />
                                  </IonCol>
                                </IonRow>
                              </IonGrid>
                            </div>
                          ))}
                        </div>
                      ))}
                      {!getJourneyForTasksQuery.data?.journey.expertArchived && (
                        <div className={styles.addTask}>
                          <Button expand="block" onClick={handleTaskCreate}>
                            <FormattedMessage id="pages.expert.dashboard.addTask" />
                          </Button>
                        </div>
                      )}
                      <IonInfiniteScroll
                        disabled={getJourneyTasksQuery.data?.journeyTasks.pageInfo?.hasNextPage === false}
                        onIonInfinite={async (e: CustomEvent<void>) => searchNext(e)}
                        position="bottom"
                        threshold="100px"
                      >
                        <IonInfiniteScrollContent />
                      </IonInfiniteScroll>
                    </IonContent>
                  )}
                </HandleQuery>
              </div>
            )}
          </div>
          <DeleteConfirmationAlert
            messageKey="pages.expert.tasks.areYouSure"
            messageValue={
              editingTask
                ? editingTask.title ?? intl.formatMessage({ id: `models.tasks.kind.${editingTask?.kind ?? ""}` })
                : ""
            }
            messageValueKey="title"
            onDelete={() => handleTaskDestroy()}
            opener={deleteTaskOpener}
          />
        </>
      )}
    </HandleQuery>
  );
};

export default EverpresentTasks;
